import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Plus } from 'lucide-react';
import type { Page, Component } from '../ComponentEditModal.types';
import { ImageInfoItemCard, type ImageInfoItem } from './ImageInfoItemCard';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';

interface ImageInfoData {
  showTitle?: boolean;
  title?: string;
  showSubtitle?: boolean;
  subtitle?: string;
  showImageBorder?: boolean;
  layout?: string;
  columns?: number;
  imageShape?: string;
  imageSize?: string;
  spacing?: string;
  items?: ImageInfoItem[];
  customColors?: {
    background?: string;
    title?: string;
    text?: string;
    imageBorder?: string;
  };
  backgroundColor?: string;
  titleColor?: string;
  textColor?: string;
  imageBorderColor?: string;
}

interface ImageInfoEditorProps {
  formData: ImageInfoData;
  setFormData: (data: ImageInfoData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  onOpenMediaUpload: (mediaType: 'image' | 'video', assetType: string, itemIndex?: number) => void;
  currentPageComponents?: Component[];
}

export const ImageInfoEditor: React.FC<ImageInfoEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  onOpenMediaUpload,
  currentPageComponents,
}) => {
  const data = formData || {};
  const items = data.items || [];
  const themeColors = useCustomColors();

  const addInfoItem = () => {
    const newItem: ImageInfoItem = {
      id: uuidv4(),
      imageUrl: '',
      cloudfrontUrl: '',
      alt: '',
      subtitle: '',
      description: '',
      order: items.length,
    };
    setFormData({ ...data, items: [...items, newItem] });
  };

  const updateInfoItem = (index: number, field: string, value: any) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setFormData({ ...data, items: updatedItems });
  };

  const updateInfoItemFields = (index: number, fields: Record<string, any>) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], ...fields };
    setFormData({ ...data, items: updatedItems });
  };

  const removeInfoItem = (index: number) => {
    const updatedItems = items.filter((_, i) => i !== index);
    updatedItems.forEach((item, i) => {
      item.order = i;
    });
    setFormData({ ...data, items: updatedItems });
  };

  const moveInfoItem = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === items.length - 1)
    ) {
      return;
    }
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const updatedItems = [...items];
    [updatedItems[index], updatedItems[newIndex]] = [updatedItems[newIndex], updatedItems[index]];
    updatedItems.forEach((item, i) => {
      item.order = i;
    });
    setFormData({ ...data, items: updatedItems });
  };

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...data.customColors, [key]: value },
    });
  };

  // Get theme-aware color defaults
  const colorFields = getColorFieldsForComponent('image-info', themeColors);

  return (
    <div className="space-y-6">
      {/* Title and Subtitle */}
      <HeadlinesSection data={data} setFormData={setFormData} />

      {/* Layout & Display Options */}
      <LayoutSection data={data} setFormData={setFormData} />

      {/* Items Management */}
      <div className="border-b pb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Image Info Items</h3>
          <button
            type="button"
            onClick={addInfoItem}
            className="px-4 py-2 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-md flex items-center space-x-1"
          >
            <Plus size={16} />
            <span>Add Item</span>
          </button>
        </div>
        <div className="space-y-4">
          {items.map((item, index) => (
            <ImageInfoItemCard
              key={item.id}
              item={item}
              index={index}
              totalItems={items.length}
              pages={pages}
              isLoadingPages={isLoadingPages}
              currentPageComponents={currentPageComponents}
              onUpdate={(field, value) => updateInfoItem(index, field, value)}
              onUpdateFields={fields => updateInfoItemFields(index, fields)}
              onRemove={() => removeInfoItem(index)}
              onMove={direction => moveInfoItem(index, direction)}
              onUpload={() => onOpenMediaUpload('image', 'image-info', index)}
            />
          ))}
        </div>
      </div>

      {/* Custom Colors */}
      <ColorCustomizationPanel
        title="Colors (Optional)"
        description="Customize image info colors. Leave empty to use defaults."
        customColors={data.customColors as Record<string, string | null | undefined>}
        colorFields={colorFields}
        onUpdateColor={updateCustomColor}
      />
    </div>
  );
};

// Sub-components

interface HeadlinesSectionProps {
  data: ImageInfoData;
  setFormData: (data: ImageInfoData) => void;
}

const HeadlinesSection: React.FC<HeadlinesSectionProps> = ({ data, setFormData }) => (
  <div className="border-b pb-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Headlines</h3>
    <div className="space-y-4">
      <div>
        <label className="flex items-center space-x-2 mb-2">
          <input
            type="checkbox"
            checked={data.showTitle !== false}
            onChange={e => setFormData({ ...data, showTitle: e.target.checked })}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded-md focus:ring-primary-500"
          />
          <span className="text-sm font-medium text-gray-700">Show Title</span>
        </label>
        {data.showTitle !== false && (
          <input
            type="text"
            value={data.title || ''}
            onChange={e => setFormData({ ...data, title: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 mt-2"
            placeholder="Welcome"
          />
        )}
      </div>
      <div>
        <label className="flex items-center space-x-2 mb-2">
          <input
            type="checkbox"
            checked={data.showSubtitle || false}
            onChange={e => setFormData({ ...data, showSubtitle: e.target.checked })}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded-md focus:ring-primary-500"
          />
          <span className="text-sm font-medium text-gray-700">Show Subtitle</span>
        </label>
        {data.showSubtitle && (
          <input
            type="text"
            value={data.subtitle || ''}
            onChange={e => setFormData({ ...data, subtitle: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 mt-2"
            placeholder="Subtitle text"
          />
        )}
      </div>
    </div>
  </div>
);

interface LayoutSectionProps {
  data: ImageInfoData;
  setFormData: (data: ImageInfoData) => void;
}

const LayoutSection: React.FC<LayoutSectionProps> = ({ data, setFormData }) => (
  <div className="border-b pb-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Layout & Display</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Layout Style</label>
        <select
          value={data.layout || 'horizontal'}
          onChange={e => setFormData({ ...data, layout: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="horizontal">Horizontal</option>
          <option value="vertical">Vertical</option>
          <option value="grid">Grid</option>
        </select>
      </div>
      {data.layout === 'grid' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Columns</label>
          <select
            value={data.columns || 3}
            onChange={e => setFormData({ ...data, columns: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value={1}>1 Column</option>
            <option value={2}>2 Columns</option>
            <option value={3}>3 Columns</option>
            <option value={4}>4 Columns</option>
            <option value={5}>5 Columns</option>
            <option value={6}>6 Columns</option>
          </select>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Image Shape</label>
        <select
          value={data.imageShape || 'round'}
          onChange={e => setFormData({ ...data, imageShape: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="round">Round</option>
          <option value="square">Square</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Image Size</label>
        <select
          value={data.imageSize || 'medium'}
          onChange={e => setFormData({ ...data, imageSize: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="small">Small</option>
          <option value="medium">Medium</option>
          <option value="large">Large</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Spacing</label>
        <select
          value={data.spacing || 'normal'}
          onChange={e => setFormData({ ...data, spacing: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="compact">Compact</option>
          <option value="normal">Normal</option>
          <option value="spacious">Spacious</option>
        </select>
      </div>
      <div className="sm:col-span-2">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={data.showImageBorder !== false}
            onChange={e => setFormData({ ...data, showImageBorder: e.target.checked })}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded-md focus:ring-primary-500"
          />
          <span className="text-sm font-medium text-gray-700">Show Image Border</span>
        </label>
      </div>
    </div>
  </div>
);
